// Input validation shared by the web forms and the API (zod 4). Error
// messages are user-facing, hence Spanish. Update schemas are built from base
// objects WITHOUT defaults so a PATCH never resets fields the user did not send.
import { z } from "zod";
import {
  CLIENT_TYPES,
  DOCUMENT_TYPES,
  ID_DOCUMENT_TYPES,
  MAX_DOCUMENT_BYTES,
  PAYMENT_METHODS,
  RISK_LEVELS,
} from "./constants";
import { isValidCurp, RFC_PATTERN } from "./curp";
import { isValidDate, today } from "./dates";

/** Optional free text: trimmed; empty string becomes null (clears the field). */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo ${max} caracteres`)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

const optionalRfc = z
  .string()
  .transform((v) => v.trim().toUpperCase())
  .refine((v) => v === "" || RFC_PATTERN.test(v), "RFC con formato inválido")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

const optionalCurp = z
  .string()
  .transform((v) => v.trim().toUpperCase())
  .refine((v) => v === "" || isValidCurp(v), "CURP con formato inválido")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

const isoDate = z.string().refine(isValidDate, "Fecha inválida");

const optionalIsoDate = z
  .string()
  .transform((v) => v.trim())
  .refine((v) => v === "" || isValidDate(v), "Fecha inválida")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

export const isoMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Mes inválido");

export const passwordSchema = z
  .string()
  .min(10, "La contraseña debe tener al menos 10 caracteres")
  .max(128, "La contraseña es demasiado larga");

export const personNameSchema = z.string().trim().min(2, "Escribe tu nombre").max(120, "Máximo 120 caracteres");

// ── Companies ────────────────────────────────────────────────────────────

export const companyRegistrationSchema = z.object({
  legalName: z.string().trim().min(2, "Escribe la razón social").max(200, "Máximo 200 caracteres"),
  legalForm: optionalText(100),
  rfc: optionalRfc,
  taxAddress: optionalText(300),
  complianceOfficer: optionalText(150),
});

export const companyUpdateSchema = companyRegistrationSchema.partial().extend({
  umaValue: z
    .number("Escribe un valor numérico")
    .min(50, "El valor de la UMA parece demasiado bajo")
    .max(1000, "El valor de la UMA parece demasiado alto")
    .optional(),
});

// ── Clients ──────────────────────────────────────────────────────────────

const clientFields = z.object({
  name: z.string().trim().min(2, "Escribe el nombre o razón social").max(200, "Máximo 200 caracteres"),
  type: z.enum(CLIENT_TYPES, "Selecciona el tipo de cliente"),
  rfc: optionalRfc,
  curp: optionalCurp,
  riskLevel: z.enum(RISK_LEVELS, "Grado de Riesgo inválido"),
  pep: z.boolean(),
  birthDate: optionalIsoDate,
  incorporationDate: optionalIsoDate,
  address: optionalText(300),
  phone: optionalText(40),
  country: optionalText(80),
  idDocumentType: z.enum(ID_DOCUMENT_TYPES, "Tipo de identificación inválido").nullable().optional(),
  idDocumentNumber: optionalText(60),
  legalRepresentative: optionalText(200),
  beneficialOwner: optionalText(300),
  declaresBeneficialOwner: z.boolean(),
});

export const clientInputSchema = clientFields.extend({
  riskLevel: clientFields.shape.riskLevel.default("medium"),
  pep: clientFields.shape.pep.default(false),
  declaresBeneficialOwner: clientFields.shape.declaresBeneficialOwner.default(false),
});

export const clientUpdateSchema = clientFields.partial();

// ── Operations ───────────────────────────────────────────────────────────

const operationFields = z.object({
  clientId: z.uuid("Selecciona un cliente"),
  date: isoDate.refine((d) => d <= today(), "La fecha no puede ser futura"),
  amount: z
    .number("Escribe el monto")
    .positive("El monto debe ser mayor a cero")
    .max(100_000_000_000, "Monto demasiado alto"),
  paymentMethod: z.enum(PAYMENT_METHODS, "Selecciona la forma de pago"),
  description: optionalText(300),
});

export const operationInputSchema = operationFields;

export const operationUpdateSchema = operationFields
  .omit({ clientId: true })
  .partial()
  .extend({
    noticeFiled: z.boolean().optional(),
    noticeFolio: optionalText(60),
  });

// ── Client documents ─────────────────────────────────────────────────────

const documentFile = z
  .file("Selecciona un archivo")
  .min(1, "El archivo está vacío")
  .max(MAX_DOCUMENT_BYTES, `El archivo pesa más de ${MAX_DOCUMENT_BYTES / 1024 / 1024} MB`);

/** multipart/form-data of an upload. The file's real format is checked by the API from its bytes. */
export const documentUploadSchema = z.object({
  file: documentFile,
  type: z.enum(DOCUMENT_TYPES, "Selecciona el tipo de documento"),
  note: optionalText(200),
});

/** multipart/form-data of "read the data of this PDF" (nothing is stored). */
export const documentExtractionSchema = z.object({ file: documentFile });

// ── Compliance actions ───────────────────────────────────────────────────

export const dismissAlertSchema = z.object({ reason: optionalText(300) });

export const zeroReportFilingSchema = z.object({ folio: optionalText(60) });

// ── Team ─────────────────────────────────────────────────────────────────

export const invitationInputSchema = z.object({
  email: z.email("Correo electrónico inválido").transform((v) => v.toLowerCase()),
  role: z.enum(["company_admin", "operator"], "Selecciona el rol"),
});

export const invitationAcceptSchema = z.object({
  name: personNameSchema,
  password: passwordSchema,
});

export const profileUpdateSchema = z.object({ name: personNameSchema });

export type CompanyRegistrationInput = z.input<typeof companyRegistrationSchema>;
export type CompanyUpdateInput = z.input<typeof companyUpdateSchema>;
export type ClientInput = z.input<typeof clientInputSchema>;
export type ClientUpdateInput = z.input<typeof clientUpdateSchema>;
export type OperationInput = z.input<typeof operationInputSchema>;
export type OperationUpdateInput = z.input<typeof operationUpdateSchema>;
export type InvitationInput = z.input<typeof invitationInputSchema>;
export type DocumentUploadInput = z.input<typeof documentUploadSchema>;
